import React, { useRef, useState } from 'react';
import type { FormikHelpers, FormikProps } from 'formik';
import { Form, Formik, useFormikContext } from 'formik';
import { snakeCase } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { ConfirmModal } from '~/components/ConfirmModal';
import { Text } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import ColoredDot from '~/components/core/Atomic/ColoredDot';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useOrganization from '~/components/OrganizationContext';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';
import type {
  ClaimFlagConfigurationCreateJson,
  ClaimFlagConfigurationModel,
} from '~/server_shared/openapi/data-contracts';
import { getLobDescription } from '~/Utils/lobUtils';

import type { ClaimFlagConfiguration } from '../../../../ClaimFlags/types';
import { scopeFieldsValidation } from '../../ScopeFields/scopeFields';
import ScopeFieldsFormik from '../../ScopeFields/ScopeFieldsFormik';
import type { RemovedFlagScope } from '../getRemovedFlagScope';
import { getRemovedFlagScope, hasRemovedFlagScope } from '../getRemovedFlagScope';
import TEXTS from '../texts';

type LabelColor = ClaimFlagConfigurationModel['color'];

type ClaimFlagFormValues = {
  label: string;
  color: LabelColor;
  key: string;
  is_enabled: boolean;
  is_all_sub_orgs: boolean;
  sub_organization_ids: number[];
  is_all_lobs: boolean;
  lobs: string[];
};

const LABEL_COLORS = [
  'berry-600',
  'berry-700',
  'blue-600',
  'blueSky-700',
  'grey-600',
  'orange-700',
  'pink-600',
  'purple-600',
  'purple-700',
  'teal-600',
  'yellow-800',
] as const satisfies LabelColor[];

type ClaimFlagAddEditDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ClaimFlagConfigurationCreateJson) => void;
  editedFlag: ClaimFlagConfiguration | null;
  existingKeys: string[];
};

/** The scope the admin just dropped, named as they picked it: sub-org names and LoB descriptions. */
const NarrowedScopeWarning = ({
  removedSubOrgNames,
  removedLobDescriptions,
}: {
  removedSubOrgNames: string[];
  removedLobDescriptions: string[];
}) => (
  <AlertBanner
    alertType={AlertBanner.ALERT_TYPES.WARNING}
    withIcon
    note={
      <div className="flex flex-col gap-8">
        {removedSubOrgNames.length > 0 && (
          <Text
            variant={Text.VARIANTS.SM}
          >{`${TEXTS.narrowedScope.removedSubOrgs} ${removedSubOrgNames.join(', ')}`}</Text>
        )}
        {removedLobDescriptions.length > 0 && (
          <Text
            variant={Text.VARIANTS.SM}
          >{`${TEXTS.narrowedScope.removedLobs} ${removedLobDescriptions.join(', ')}`}</Text>
        )}
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.narrowedScope.explanation}
        </Text>
      </div>
    }
  />
);

const ClaimFlagAddEditDialogInner = ({ isEdit, onCancel }: { isEdit: boolean; onCancel: () => void }) => {
  const { values, isSubmitting } = useFormikContext<ClaimFlagFormValues>();
  useSetDefaultFieldsOnChange(values.label, { key: snakeCase(values.label) }, null, isEdit);

  return (
    <Form className="flex flex-col gap-12">
      <div className="flex gap-12">
        {/* The select's only content is a color dot, so it stays at dot+arrow width and the label takes the row. */}
        <div className="w-[96px] shrink-0">
          <TextFieldFormik select id="color" name="color" label={TEXTS.labels.color} fullWidth>
            {/* todo: remove this and next 2 lines once TextFieldFormik converts to typescript */}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {LABEL_COLORS.map((color) => (
              <MenuItem key={color} value={color}>
                <ColoredDot color={color} />
              </MenuItem>
            ))}
          </TextFieldFormik>
        </div>
        <div className="grow">
          <TextFieldFormik id="label" name="label" label={TEXTS.labels.label} fullWidth />
        </div>
      </div>
      <TextFieldFormik id="key" name="key" label={TEXTS.labels.key} disabled={isEdit} fullWidth />
      <ScopeFieldsFormik />
      <div className="flex justify-end">
        <Button variant="outlined" color="primary" className="mr-16" onClick={onCancel} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {TEXTS.save}
        </Button>
      </div>
    </Form>
  );
};

const ClaimFlagAddEditDialog = ({
  isOpen,
  onClose,
  onSubmit,
  editedFlag,
  existingKeys,
}: ClaimFlagAddEditDialogProps) => {
  const { subOrganizations, subOrganizationEnabled, supportedClaimTypes } = useOrganization() as {
    subOrganizations?: { id: number; name: string }[];
    subOrganizationEnabled?: boolean;
    supportedClaimTypes?: string[];
  };
  const { lobConfigurationsDict } = useLobConfiguration();
  const formikRef = useRef<FormikProps<ClaimFlagFormValues>>(null);
  const [removedScope, setRemovedScope] = useState<RemovedFlagScope | null>(null);
  // Consumed by the next submit: the re-submit the warning's confirm button fires must not re-open it.
  const isNarrowingConfirmed = useRef(false);

  const subOrgs = subOrganizationEnabled ? (subOrganizations ?? []) : [];
  const allSubOrgIds = subOrgs.map((subOrg) => subOrg.id);

  // Gate the whole submission (not just the Save button) so an Enter-key submit is warned about too.
  const handleSubmit = (values: ClaimFlagFormValues, { setSubmitting }: FormikHelpers<ClaimFlagFormValues>) => {
    const flagValues = {
      ...values,
      sub_organization_ids: values.is_all_sub_orgs ? [] : values.sub_organization_ids,
      lobs: values.is_all_lobs ? [] : values.lobs,
    };
    const wasConfirmed = isNarrowingConfirmed.current;
    isNarrowingConfirmed.current = false;

    if (editedFlag && !wasConfirmed) {
      const removed = getRemovedFlagScope(editedFlag, flagValues, {
        subOrganizationIds: allSubOrgIds,
        // `is_all_lobs` matches a claim's LoB whatever it is (`isFlagInClaimScope`, mirrored by
        // `claim_flag_manager._flag_matches_claim_scope`), so its universe is the org's whole claim-type
        // list — not the sub-org-filtered set the LoB picker offers. That way an all-LoBs scope the admin
        // enumerates by hand still warns about an org-wide LoB the picker never offered, exactly where it
        // matters: a claim whose LoB the selected sub-orgs don't support loses the match.
        lobs: supportedClaimTypes ?? [],
      });
      if (hasRemovedFlagScope(removed)) {
        // Release the submission so the form stays usable while the admin decides.
        setSubmitting(false);
        setRemovedScope(removed);
        return;
      }
    }

    return onSubmit(flagValues);
  };

  const confirmNarrowedScope = () => {
    isNarrowingConfirmed.current = true;
    setRemovedScope(null);
    // Re-submit through Formik so the save keeps its submitting state (and disabled footer) as usual.
    // Clear the flag once that settles: a submitForm() that short-circuits on validation never reaches
    // handleSubmit, and the dialog outlives a single save, so a flag left set would silently skip the
    // next save's warning. submitForm() rethrows whatever onSubmit rejected with, and this re-submit has
    // nothing to add to the caller's own error handling, so the rejection is swallowed rather than left
    // unhandled.
    void Promise.resolve(formikRef.current?.submitForm())
      .catch(() => {})
      .finally(() => {
        isNarrowingConfirmed.current = false;
      });
  };

  return (
    <CardDialog
      isDialog
      fullWidth
      open={isOpen}
      onClose={onClose}
      title={editedFlag ? TEXTS.editFlag : TEXTS.createFlag}
    >
      <Formik<ClaimFlagFormValues>
        innerRef={formikRef}
        initialValues={{
          label: editedFlag?.label || '',
          color: editedFlag?.color || 'blue-600',
          key: editedFlag?.key || '',
          is_enabled: editedFlag?.is_enabled ?? true,
          is_all_sub_orgs: editedFlag?.is_all_sub_orgs ?? true,
          sub_organization_ids: editedFlag?.sub_organization_ids ?? [],
          is_all_lobs: editedFlag?.is_all_lobs ?? true,
          lobs: editedFlag?.lobs ?? [],
        }}
        validationSchema={Yup.object().shape({
          label: Yup.string().trim().max(255).required(TEXTS.required),
          ...(editedFlag
            ? {}
            : {
                key: Yup.string()
                  .trim()
                  .max(128)
                  .notOneOf(existingKeys, TEXTS.keyMustBeUnique)
                  .required(TEXTS.required)
                  .matches(/^[a-z0-9_]+$/, TEXTS.keyMustMatchPattern)
                  .matches(/[a-z0-9]/, TEXTS.keyMustContainLetterOrNumber),
              }),
          ...scopeFieldsValidation,
        })}
        onSubmit={handleSubmit}
      >
        <ClaimFlagAddEditDialogInner isEdit={!!editedFlag} onCancel={onClose} />
      </Formik>
      {removedScope && (
        <ConfirmModal
          isOpen
          title={TEXTS.narrowedScope.title}
          contentText={
            <NarrowedScopeWarning
              // A stored scope can name a sub-org the org context no longer lists (since deleted), so fall
              // back to its id rather than dropping it from the warning.
              removedSubOrgNames={removedScope.subOrganizationIds.map(
                (id) => subOrgs.find((subOrg) => subOrg.id === id)?.name ?? String(id)
              )}
              removedLobDescriptions={removedScope.lobs.map(
                (lob) => getLobDescription(lob, lobConfigurationsDict) || lob
              )}
            />
          }
          primaryButtonName={TEXTS.narrowedScope.confirm}
          secondaryButtonName={TEXTS.narrowedScope.keepEditing}
          onPrimaryBtnClick={confirmNarrowedScope}
          onClose={() => setRemovedScope(null)}
        />
      )}
    </CardDialog>
  );
};

export default ClaimFlagAddEditDialog;
